import { useEffect, useRef } from "react"
import { waitForPendingWrites } from "firebase/firestore"
import { toast } from "react-toastify"
import { auth, firestoreDB } from "./firebase.service"
import {
	countCalendarFailure,
	readCalendarQueue,
	removeCalendarRepair,
} from "./calendarQueue.service"
import {
	getCoachLessonsInRange,
	getLessonForCalendar,
	getLessonTypes,
	getSchool,
	saveLessonEventID,
} from "../utils/firestore.utils"
import {
	isNotConnectedError,
	isReconnectError,
	openCalendarSession,
} from "../providers/profile.provider"
import {
	buildEventTitle,
	calendarAvailable,
	calendarCheckDay,
	calendarCheckRange,
	calendarEventID,
	eventMatchesLesson,
	expectedLessonEvent,
	monthRange,
} from "../utils/calendar.utils"
import { resolveLessonTypes } from "../utils/school.utils"
import { withMatchLessonType } from "../utils/match.utils"
import { isCancelledLesson } from "../utils/cancellation.utils"
import { buildEventLocation } from "../utils/func.utils"
import { isNetworkError, isOffline, requireNetwork } from "../utils/network.utils"

// ── IL CALENDARIO SI RIMETTE A POSTO DA SOLO ───────────────────────
// Google Calendar senza rete non si raggiunge: la lezione parte quando la
// rete torna, l'evento no. Due cose lo rimettono a posto senza che il
// maestro debba farci niente:
//
// - la coda (services/calendarQueue.service.js): ogni operazione sul
//   calendario che non è andata si segna sul telefono, e si rifà quando
//   l'app si apre, torna in primo piano o ritrova la rete;
//
// - il controllo di ogni giorno: la prima volta che l'app si apre con la
//   rete, le lezioni del maestro di questo mese e del prossimo si
//   confrontano con il suo calendario, e quello che non torna si sistema.
//   Copre quello che la coda non vede: una lezione cambiata senza rete da
//   un altro dispositivo, un telefono a cui sono stati cancellati i dati.
//
// Lo stesso controllo, su un mese a scelta, si fa a mano dal profilo
// (`checkCalendarNow`). Il controllo aggiunge e corregge eventi, non ne
// toglie mai: un evento che non è di nessuna lezione può averlo messo il
// maestro, e non si tocca. Gli eventi da togliere li porta la coda.

const CHECK_STORAGE_KEY = "incampo.calendarCheck"

// Aspettare che le lezioni scritte senza rete siano arrivate al server:
// prima, per il server non ci sono, e la loro riparazione le darebbe per
// eliminate. Se non arrivano in tempo si riprova alla prossima occasione.
const FIRESTORE_SETTLE_TIMEOUT = 20000

// All'apertura l'app ha di meglio da fare: il controllo parte dopo.
const ROUTINE_START_DELAY = 5000

const firestoreSettled = () =>
	Promise.race([
		waitForPendingWrites(firestoreDB).then(() => true),
		new Promise(resolve => setTimeout(() => resolve(false), FIRESTORE_SETTLE_TIMEOUT)),
	])

const calendarConnected = coach =>
	coach?.googleCalendar?.connected === true && coach?.googleCalendar?.needsReconnect !== true

// I circoli del maestro. `schoolIDs` è la copia in stringhe che tiene il
// server; teachesIn, managedSchools e notTeachingIn ci sono per chi non
// l'ha ancora.
const coachSchoolIDs = coach => [
	...new Set([
		...(coach?.schoolIDs ?? []),
		...(coach?.teachesIn ?? []),
		...(coach?.managedSchools ?? []),
		...(coach?.notTeachingIn ?? []),
	]),
]

const lastCheckDay = coachID => {
	try {
		return window.localStorage.getItem(`${CHECK_STORAGE_KEY}.${coachID}`)
	} catch {
		return null
	}
}

const rememberCheckDay = coachID => {
	try {
		window.localStorage.setItem(`${CHECK_STORAGE_KEY}.${coachID}`, calendarCheckDay())
	} catch {}
}

// Titolo e luogo dell'evento di una lezione, dai tipi di lezione e dal
// circolo, letti una volta per giro. `null` se non si sa come chiamarla —
// un tipo di lezione che non c'è più, un circolo che non si legge: meglio
// non toccare l'evento che riscriverlo sbagliato.
const createLessonEventResolver = () => {
	let catalog = null
	const schools = new Map()

	return async lesson => {
		if (!catalog) catalog = await getLessonTypes()
		if (!schools.has(lesson.schoolID)) schools.set(lesson.schoolID, getSchool(lesson.schoolID))
		const school = await schools.get(lesson.schoolID)
		if (!school) return null

		const lessonTypeName = withMatchLessonType(resolveLessonTypes(catalog, school.lessonTypes))[
			lesson.lessonType
		]?.lessonTypeName
		if (!lessonTypeName) return null

		return {
			eventTitle: buildEventTitle(
				lessonTypeName,
				lesson.lessonType === "racchetteInClasse" ? [] : lesson.partecipants,
			),
			eventLocation: buildEventLocation(school),
		}
	}
}

// Un calendario per maestro, e un token per calendario per tutto il giro
const createSessions = () => {
	const sessions = new Map()
	return coachID => {
		if (!sessions.has(coachID)) sessions.set(coachID, openCalendarSession(coachID))
		return sessions.get(coachID)
	}
}

// Mette l'evento come la lezione, e scrive sulla lezione l'ID dell'evento se
// è cambiato. Quella scrittura può non passare — una lezione che il
// direttore ha messo al maestro, il maestro non la modifica — e non importa:
// l'evento ha l'ID ricavato dalla lezione, e la volta dopo lo si ritrova.
const repairLessonEvent = async (session, lesson, eventData) => {
	const eventID = await session.saveEvent({ ...lesson, ...eventData })
	if (eventID && eventID !== lesson.eventID) {
		saveLessonEventID(lesson.lessonID, eventID).catch(err =>
			console.info("eventID non scritto sulla lezione:", err?.code ?? err),
		)
	}
}

// ── LA CODA ────────────────────────────────────────────────────────
// Torna quante operazioni sono andate a buon fine.
const replayCalendarQueue = async uid => {
	const queue = readCalendarQueue(uid)
	if (queue.length === 0) return 0

	const sessionFor = createSessions()
	const resolveEvent = createLessonEventResolver()
	let repaired = 0

	for (const entry of queue) {
		if (isOffline()) break
		try {
			if (entry.type === "delete") {
				await (await sessionFor(entry.coachID)).deleteEvent(entry.eventID)
				repaired++
			} else {
				// eliminata o annullata nel frattempo: niente evento da fare.
				// Se c'era, lo toglie la sua voce in coda.
				const lesson = await getLessonForCalendar(entry.lessonID)
				if (lesson?.coachID && !isCancelledLesson(lesson)) {
					const eventData = await resolveEvent(lesson)
					if (!eventData) throw new Error("Tipo di lezione o circolo non leggibili")
					await repairLessonEvent(await sessionFor(lesson.coachID), lesson, eventData)
					repaired++
				}
			}
			removeCalendarRepair(uid, entry.id)
		} catch (err) {
			// quel calendario non è più collegato: non c'è dove rifarla
			if (isNotConnectedError(err)) {
				removeCalendarRepair(uid, entry.id)
				continue
			}
			// senza rete non va nessuna: si riprova la prossima volta
			if (isNetworkError(err)) break
			// collegamento da rifare: lo dice il profilo, e la voce aspetta
			if (isReconnectError(err)) continue
			console.error("Operazione sul calendario non riuscita:", err)
			countCalendarFailure(uid, entry.id)
		}
	}

	return repaired
}

// ── IL CONTROLLO ───────────────────────────────────────────────────
// Le lezioni del maestro nel periodo, confrontate con il suo calendario.
const checkCalendar = async (coach, { start, end }) => {
	const lessonsBySchool = await Promise.all(
		coachSchoolIDs(coach).map(schoolID =>
			getCoachLessonsInRange(schoolID, coach.coachID, start, end).catch(err => {
				// un circolo che il token non conosce più: si controllano gli altri
				if (err?.code === "permission-denied") return []
				throw err
			}),
		),
	)
	// una lezione annullata il suo evento non ce l'ha più, apposta
	const lessons = lessonsBySchool.flat().filter(lesson => !isCancelledLesson(lesson))
	if (lessons.length === 0) return { lessons: 0, repaired: 0, skipped: 0, failed: 0 }

	const session = await openCalendarSession(coach.coachID)
	const events = await session.listEvents(start, end)
	const eventsByID = new Map(events.map(event => [event.id, event]))
	const resolveEvent = createLessonEventResolver()
	let repaired = 0
	let skipped = 0
	let failed = 0

	for (const lesson of lessons) {
		const eventData = await resolveEvent(lesson)
		if (!eventData) {
			skipped++
			continue
		}
		const event = eventsByID.get(lesson.eventID || calendarEventID(lesson.lessonID))
		if (eventMatchesLesson(event, expectedLessonEvent(lesson, eventData))) continue

		try {
			await repairLessonEvent(session, lesson, eventData)
			repaired++
		} catch (err) {
			// senza rete o col collegamento scaduto non ne passerà nessuna
			if (isNetworkError(err) || isReconnectError(err)) throw err
			// un evento che Google rifiuta non ferma le altre lezioni
			console.error(`Evento della lezione ${lesson.lessonID} non sistemato:`, err)
			failed++
		}
	}

	return { lessons: lessons.length, repaired, skipped, failed }
}

const announceRepairs = repaired => {
	if (repaired === 0) return
	toast.info(
		repaired === 1
			? "Google Calendar aggiornato: un evento sistemato"
			: `Google Calendar aggiornato: ${repaired} eventi sistemati`,
		{ toastId: "calendar-repaired", position: "top-center", autoClose: 6000 },
	)
}

// ── IN SOTTOFONDO ──────────────────────────────────────────────────
// Un giro alla volta: apertura, rete ritrovata e app in primo piano possono
// arrivare insieme.
let routine = null

export const runCalendarRoutine = coach => {
	if (routine) return routine

	routine = (async () => {
		const uid = auth?.currentUser?.uid
		if (!uid || !coach?.coachID || isOffline()) return

		const needsCheck =
			calendarConnected(coach) && lastCheckDay(coach.coachID) !== calendarCheckDay()
		if (readCalendarQueue(uid).length === 0 && !needsCheck) return
		if (!(await firestoreSettled())) return

		let repaired = await replayCalendarQueue(uid)

		if (needsCheck) {
			const result = await checkCalendar(coach, calendarCheckRange())
			rememberCheckDay(coach.coachID)
			repaired += result.repaired
		}

		announceRepairs(repaired)
	})()
		// nessuno lo aspetta: si riprova alla prossima occasione
		.catch(err => console.error("Controllo del calendario non riuscito:", err))
		.finally(() => {
			routine = null
		})

	return routine
}

// Si monta una volta, in App: parte poco dopo l'apertura, e di nuovo quando
// torna la rete o l'app torna in primo piano. Il controllo vero si fa una
// volta al giorno, la coda ogni volta che c'è qualcosa dentro.
export const useCalendarRoutine = coach => {
	const latestCoach = useRef(coach)
	latestCoach.current = coach
	const coachID = coach?.coachID

	useEffect(() => {
		// sul progetto dev non c'è un calendario da controllare
		if (!coachID || !calendarAvailable()) return
		const run = () => runCalendarRoutine(latestCoach.current)
		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") run()
		}

		const timer = setTimeout(run, ROUTINE_START_DELAY)
		window.addEventListener("online", run)
		document.addEventListener("visibilitychange", onVisibilityChange)
		return () => {
			clearTimeout(timer)
			window.removeEventListener("online", run)
			document.removeEventListener("visibilitychange", onVisibilityChange)
		}
	}, [coachID])
}

// ── A MANO, DAL PROFILO ────────────────────────────────────────────
// Lo stesso controllo, sul mese che si sceglie, e intanto la coda. Torna
// quante lezioni ci sono nel mese, quanti eventi si sono sistemati e quante
// lezioni non si sono potute controllare.
export const checkCalendarNow = async (coach, month) => {
	requireNetwork("Per controllare il calendario serve la rete")
	const uid = auth?.currentUser?.uid
	await firestoreSettled()

	const replayed = uid ? await replayCalendarQueue(uid) : 0
	const result = await checkCalendar(coach, monthRange(month))
	return { ...result, repaired: result.repaired + replayed }
}
